// Listing modes for Supplier Invoices — List / Bill Process.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/quotes/modes.ts and src/ads-and-sbs/modes.ts (Rule 0 — the canonical reference).
//
//   <base>/list      → SupplierInvoiceList  (the working register)
//   <base>/process   → BillProcess          (drop an invoice/bill, find and close PO)
//
// ── BILL PROCESS ON EVERY TENANT (2026-10-14) ───────────────────────────────
// It was gated to g2 and aquipe because one image serves every tenant. Prod
// (gitlab/develop src/supplierinvoices/modes.ts, ec3b7dce2) ships List and Bill
// Process to every customer, and mustang had no drop zone in the 2026-10-13
// demo, so the vibe stack now matches prod. The function stays so the route
// table in SupplierInvoices.tsx keeps one shape with prod's.
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in SupplierInvoices.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  process: "Bill Process",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Bills and Xero status",
  process: "Match bills to POs",
};

/**
 * List, not Process. The register IS the working surface — someone opens this
 * module to view supplier invoices. Process is where you go to ingest a supplier
 * invoice / bill and match it against purchase orders.
 */
export const DEFAULT_MODE_SEGMENT = "list";

/**
 * `<module>.viewMode`, underscored — the convention
 * src/__tests__/routeModeSegments.test.tsx pins for tech_log
 * and src/quotes/modes.ts follows.
 */
export const MODE_STORAGE_KEY = "supplier_invoices.viewMode";

/** Does this tenant get the mode switcher? Every tenant does; see the note above. */
export const hasModeSwitcher = (): boolean => true;

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
